import React, { useState, useEffect } from "react";
import { X, Percent, DollarSign } from "lucide-react";

export default function POLineMarkupModal({
  isOpen,
  onClose,
  item,
  po,
  defaultMarkup = 15,
  onSave
}) {
  const [markupType, setMarkupType] = useState("percent"); // "percent" | "dollar"
  const [markupVal, setMarkupVal] = useState("");

  useEffect(() => {
    if (isOpen && item) {
      if (item.markupType === "dollar" && item.markupDollar !== undefined && item.markupDollar !== null) {
        setMarkupType("dollar");
        setMarkupVal(String(item.markupDollar));
      } else if (item.markupPercentage !== undefined && item.markupPercentage !== null) {
        setMarkupType("percent");
        setMarkupVal(String(item.markupPercentage));
      } else {
        setMarkupType("percent");
        setMarkupVal("");
      }
    }
  }, [isOpen, item]);

  if (!isOpen || !item) return null;

  // Calculate Base Line Cost for preview
  const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [
    { quantity: item.quantity, vendorPrice: item.vendorPrice, shippingCost: item.shippingCost }
  ];
  const lineTotalCost = itemVendors.reduce((vSum, v) => {
    return vSum + (Number(v.quantity || 0) * Number(v.vendorPrice || 0)) + Number(v.shippingCost || 0);
  }, 0);

  const poEstShipping = Number(po?.estShippingCost || 0);
  const totalItemsCount = po?.items?.length || 1;
  const shippingPad = (poEstShipping > 0 && totalItemsCount > 0)
    ? (poEstShipping / totalItemsCount)
    : 0;

  let previewClientPrice = 0;
  if (markupVal === "") {
    previewClientPrice = (lineTotalCost * (1 + Number(defaultMarkup || 0) / 100)) + shippingPad;
  } else if (markupType === "dollar") {
    previewClientPrice = lineTotalCost + Number(markupVal || 0) + shippingPad;
  } else {
    previewClientPrice = (lineTotalCost * (1 + Number(markupVal || 0) / 100)) + shippingPad;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (markupVal === "" || isNaN(Number(markupVal))) {
      onSave(null);
    } else {
      onSave({
        type: markupType,
        value: Number(markupVal)
      });
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: "420px" }}>
        <div className="modal-header">
          <h3>Line Item Markup</h3>
          <button className="modal-close-btn" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: "1.5rem" }}>
          <div style={{ marginBottom: "1.25rem" }}>
            <span className="procurement-modal-label" style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>Item Description</span>
            <strong className="procurement-modal-item-name" style={{ display: "block", marginTop: "0.25rem", color: "var(--text-primary)" }}>
              {item.description || item.name || "Unnamed Item"}
            </strong>
          </div>

          {/* Segmented Toggle for Markup Mode */}
          <div style={{ marginBottom: "1.25rem" }}>
            <label className="form-label" style={{ display: "block", marginBottom: "0.5rem" }}>Markup Type</label>
            <div style={{ display: "flex", gap: "0.5rem", background: "var(--card-bg-light, rgba(255,255,255,0.05))", padding: "4px", borderRadius: "8px", border: "1px solid var(--border-color)" }}>
              <button
                type="button"
                className={`btn ${markupType === "percent" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setMarkupType("percent")}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.4rem",
                  padding: "0.4rem 0.75rem",
                  fontSize: "0.85rem",
                  borderRadius: "6px"
                }}
              >
                <Percent size={14} /> Percentage (%)
              </button>
              <button
                type="button"
                className={`btn ${markupType === "dollar" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setMarkupType("dollar")}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.4rem",
                  padding: "0.4rem 0.75rem",
                  fontSize: "0.85rem",
                  borderRadius: "6px"
                }}
              >
                <DollarSign size={14} /> Fixed Dollar ($)
              </button>
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: "1.25rem" }}>
            <label className="form-label" htmlFor="lineMarkupInput">
              {markupType === "percent" ? "Markup Percentage (%)" : "Markup Amount ($)"}
            </label>
            <div style={{ position: "relative" }}>
              <input
                id="lineMarkupInput"
                type="number"
                step="any"
                min="0"
                className="form-input"
                value={markupVal}
                onChange={(e) => setMarkupVal(e.target.value)}
                placeholder={markupType === "percent" ? `Inherit Default (${defaultMarkup}%)` : "$0.00"}
                autoFocus
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
              />
            </div>
            <p style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: "0.5rem" }}>
              {markupType === "percent"
                ? "Leave blank to inherit the EST's overall markup rate."
                : "Enter a fixed dollar markup to add directly on top of line cost."}
            </p>
          </div>

          {/* Real-time Calculation Preview */}
          <div style={{ 
            padding: "0.75rem 1rem", 
            backgroundColor: "rgba(52, 152, 219, 0.08)", 
            borderRadius: "6px", 
            border: "1px solid rgba(52, 152, 219, 0.2)",
            marginBottom: "1.5rem" 
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: "0.25rem" }}>
              <span>Base Line Cost:</span>
              <span>${lineTotalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", fontWeight: 600, color: "var(--text-primary)" }}>
              <span>Estimated Client Charge:</span>
              <span style={{ color: "var(--primary)" }}>
                ${previewClientPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          <div className="procurement-modal-actions" style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => {
                setMarkupVal("");
                setMarkupType("percent");
              }} 
              style={{ marginRight: "auto" }}
            >
              Reset to Default
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Markup
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
